/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useContext, useState } from 'react';

import type { LearnerProfile_EducationHistory as EducationHistory } from '__generated__/graphql-types-do-not-use';

import { Button, ContextualHelp, Grid, InlineNotification, Typography, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import CredentialsInfoDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsInfoDialog';
import CredentialsSectionBody from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsSectionBody';
import CredentialsSectionEducationEditDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/credentials/CredentialsSectionEducationEditDialog';
import { onlyCourseraIssuedBadges } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const styles = {
  sectionTitle: () => css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
};

const E2E_SELECTORS = {
  EDUCATION_SECTION: 'profile-education-section',
  EDUCATION_SECTION_ADD: 'profile-education-section-add',
} as const;

const CredentialsSection = () => {
  const { isProfileLoading, profileState = {}, editable, courseraHistoryFailedToLoad } = useContext(ProfileContext);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedEducation, setSelectedEducation] = useState<EducationHistory | undefined>();
  const {
    badges,
    educationHistory = [],
    courseraHistory = {
      completedProfessionalCertificates: [],
      completedSpecializations: [],
    },
  } = profileState;

  const handleOpenEducationDialog = (education?: EducationHistory) => {
    setSelectedEducation(education);
    setIsDialogOpen(true);
  };

  const handleCloseEducationDialog = () => {
    setSelectedEducation(undefined);
    setIsDialogOpen(false);
  };

  const noCourseraHistory =
    courseraHistory?.completedProfessionalCertificates?.length === 0 &&
    courseraHistory?.completedSpecializations?.length === 0 &&
    !courseraHistoryFailedToLoad;

  const courseraIssuedBadges = onlyCourseraIssuedBadges(badges?.badges ?? []);

  const hasBadges = courseraIssuedBadges.length > 0;
  /**
   * Does not render section when it's not in edit mode and there is no education and coursera history data
   * unless there is a coursera history error detected so we can show a specific error message as it's not
   * possible to distinguish between no data and error state.
   * */

  const noEducationHistory = educationHistory.length === 0 && courseraIssuedBadges.length === 0;
  if (!isProfileLoading && !editable && noEducationHistory && noCourseraHistory) {
    return null;
  }

  return (
    <ProfileViewerCard data-e2e={E2E_SELECTORS.EDUCATION_SECTION}>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" css={styles.sectionTitle}>
        <Grid item>
          <ContextualHelp
            helpToggleProps={{
              'aria-label': _t('Information about credentials section'),
            }}
            label={
              <Typography component="h3" variant="h2semibold" data-testid="credentials-section-title">
                {_t('Credentials')}
              </Typography>
            }
          >
            {({ onClose, isExpanded = false }) => (
              <CredentialsInfoDialog isOpen={isExpanded} onClose={onClose} includeBadgeInfo={hasBadges} />
            )}
          </ContextualHelp>
        </Grid>
        <Grid item>
          {editable && educationHistory.length > 0 && (
            <Button
              variant="secondary"
              iconPosition="before"
              icon={<AddIcon />}
              size="small"
              onClick={() => handleOpenEducationDialog()}
              component={TrackedButton}
              data-e2e={E2E_SELECTORS.EDUCATION_SECTION_ADD}
              requireFullyVisible={false}
              withVisibilityTracking={true}
              // not tracked in eventing v3
              trackingName="profile_add_education_history"
            >
              {_t('Add')}
            </Button>
          )}
        </Grid>
      </Grid>
      <CredentialsSectionBody openEducationDialog={handleOpenEducationDialog} />
      {editable && (
        <CredentialsSectionEducationEditDialog
          isOpen={isDialogOpen}
          education={selectedEducation}
          handleClose={handleCloseEducationDialog}
        />
      )}
    </ProfileViewerCard>
  );
};

const ErrorBoundCredentialsSection = () => (
  <ErrorBoundaryWithTagging
    componentName="CredentialsSection"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the credentials section of the page. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <CredentialsSection />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundCredentialsSection;
